import { useCallback, useEffect, useState } from "react";
import {
  getFlightBookings,
  getFlightBookingsSummary,
  searchFlightBookings,
} from "../../../services/booking-service";

export const TABS = [
  {
    key: "all",
    label: "All",
    summaryKey: "all",
    params: { type: "FLIGHT,FLIGHT-RESERVATION" },
  },
  {
    key: "ticketed",
    label: "Ticketed",
    summaryKey: "ticketed",
    params: { type: "FLIGHT,FLIGHT-RESERVATION", ticketed: "yes" },
  },
  {
    key: "reserved",
    label: "Reserved",
    summaryKey: "reserved",
    params: {
      type: "FLIGHT,FLIGHT-RESERVATION",
      ticketed: "no",
      status: "PENDING,CONFIRMED",
    },
  },
  {
    key: "unpaid",
    label: "Unpaid",
    summaryKey: "unpaid",
    params: {
      type: "FLIGHT,FLIGHT-RESERVATION",
      payment_status: "PENDING",
      status: "PENDING",
    },
  },
  {
    key: "cancelled",
    label: "Cancelled",
    summaryKey: "cancelled",
    params: { type: "FLIGHT,FLIGHT-RESERVATION", status: "CANCELLED" },
  },
  {
    key: "needsAttention",
    label: "Needs attention",
    summaryKey: "needsAttention",
    params: {
      type: "FLIGHT,FLIGHT-RESERVATION",
      cancellationState: "PENDING_PROVIDER,FAILED",
    },
  },
];

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

export const useFlightBookings = () => {
  const [activeTab, setActiveTabState] = useState("all");
  const [page, setPage] = useState(1);
  // searchInput is what the controlled <Input> renders — never trimmed, so
  // a trailing space while typing doesn't get eaten on every keystroke.
  // searchTerm is the debounced value the request actually fires on.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState({ bookings: [], metadata: {} });
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchSummary = useCallback(() => {
    getFlightBookingsSummary()
      .then(setSummary)
      .catch((err) => console.error("Error fetching flight summary:", err));
  }, []);

  const fetchPage = useCallback(() => {
    setLoading(true);
    const tab = TABS.find((t) => t.key === activeTab) ?? TABS[0];
    const trimmedSearch = searchTerm.trim();

    const request = trimmedSearch
      ? searchFlightBookings(trimmedSearch, tab.params.type)
      : getFlightBookings({ ...tab.params, page, pageSize: PAGE_SIZE });

    request
      .then((response) => setData(response))
      .catch((err) => console.error("Error fetching flight bookings:", err))
      .finally(() => setLoading(false));
  }, [activeTab, page, searchTerm]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const setActiveTab = (key) => {
    setActiveTabState(key);
    setPage(1);
    setSearchInput("");
    setSearchTerm("");
  };

  const refetch = () => {
    fetchPage();
    fetchSummary();
  };

  return {
    activeTab,
    setActiveTab,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    searchInput,
    setSearchInput,
    data,
    loading,
    summary,
    refetch,
  };
};
